import React, { useMemo, useState } from 'react';
import {
  formatBomLengthMeters,
  layoutBomCategoryFilter,
  layoutBomMatchesSearch,
  layoutBomToSpreadsheetXml,
  type LayoutBomCategory,
  type LayoutBomFilterCategory,
  type LayoutBomResult,
  type LayoutBomRow,
} from '../../utils/bom';
import { formatBomCurrency } from '../../utils/bomPricing';

const FILTER_OPTIONS: LayoutBomFilterCategory[] = [
  'All',
  'Tracks',
  'Stockers',
  // 'Equipment',
  'OHTs',
  'Infrastructure',
  'Other',
];

const CATEGORY_PILL: Record<LayoutBomCategory, string> = {
  Track: 'bg-sky-50 text-sky-700',
  Stocker: 'bg-violet-50 text-violet-700',
  Equipment: 'bg-slate-100 text-slate-700',
  OHTs: 'bg-cyan-50 text-cyan-700',
  Infrastructure: 'bg-amber-50 text-amber-700',
  Other: 'bg-gray-100 text-gray-600',
};

const COST_SOURCE_LABEL: Record<NonNullable<LayoutBomRow['costSource']>, string> = {
  element: 'Unit cost set on the layout element',
  catalog: 'Unit cost from the toolbox catalog',
  'price-book': 'Estimate from the default price book',
  none: 'No unit cost available',
};

type BomPreviewModalProps = {
  bom: LayoutBomResult;
  onClose: () => void;
};

const Chevron: React.FC<{ open: boolean }> = ({ open }) => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 20 20"
    fill="none"
    className={`shrink-0 text-slate-400 transition-transform ${open ? 'rotate-90' : ''}`}
    aria-hidden
  >
    <path
      d="M7 5l6 5-6 5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SummaryStat: React.FC<{ label: string; value: string | number }> = ({ label, value }) => (
  <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
    <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">{label}</div>
    <div className="mt-0.5 text-sm font-semibold tabular-nums text-slate-900">{value}</div>
  </div>
);

const CategoryPill: React.FC<{ category: LayoutBomCategory }> = ({ category }) => (
  <span
    className={`inline-flex rounded-full px-2.5 py-0.5 text-[11px] font-medium ${CATEGORY_PILL[category]}`}
  >
    {category}
  </span>
);

const InstallationComponentsPanel: React.FC<{
  itemNo: number;
  items: NonNullable<LayoutBomRow['installationComponents']>;
}> = ({ itemNo, items }) => (
  <div className="mx-6 mt-3 mb-3 rounded-xl border border-slate-200 bg-slate-50/70">
    <div className="px-4 py-2.5 text-sm font-medium text-slate-800">
      Installation Components ({items.length})
    </div>
    <div className="overflow-hidden rounded-b-xl border-t border-slate-200 bg-white">
      <table className="min-w-full text-xs">
        <thead className="text-slate-400">
          <tr>
            <th className="px-4 py-2 text-left font-medium">#</th>
            <th className="px-3 py-2 text-left font-medium">Component</th>
            <th className="px-3 py-2 text-left font-medium">Category</th>
            <th className="px-3 py-2 text-left font-medium">Description</th>
            <th className="px-3 py-2 text-right font-medium">Qty (per unit)</th>
            <th className="px-3 py-2 text-right font-medium">Total Qty</th>
            <th className="px-4 py-2 text-left font-medium">Unit</th>
          </tr>
        </thead>
        <tbody className="text-slate-700">
          {items.map((item, index) => (
            <tr key={item.id} className="border-t border-slate-100">
              <td className="px-4 py-2 tabular-nums whitespace-nowrap text-slate-500">
                {itemNo}.{index + 1}
              </td>
              <td className="px-3 py-2">{item.name}</td>
              <td className="px-3 py-2 text-slate-500">{item.category || '—'}</td>
              <td className="px-3 py-2 text-slate-500">{item.description || '—'}</td>
              <td className="px-3 py-2 text-right tabular-nums">{item.qtyPerUnit}</td>
              <td className="px-3 py-2 text-right font-semibold tabular-nums">{item.totalQty}</td>
              <td className="px-4 py-2">{item.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

export const BomPreviewModal: React.FC<BomPreviewModalProps> = ({ bom, onClose }) => {
  const [filter, setFilter] = useState<LayoutBomFilterCategory>('All');
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const visibleRows = useMemo(
    () =>
      bom.rows.filter(
        (row) => layoutBomCategoryFilter(row, filter) && layoutBomMatchesSearch(row, search)
      ),
    [bom.rows, filter, search]
  );

  const handleExportCsv = () => {
    const xml = layoutBomToSpreadsheetXml(bom.rows);
    const blob = new Blob([xml], { type: 'application/vnd.ms-excel;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `layout-bom-${new Date().toISOString().slice(0, 10)}.xls`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const toggleExpanded = (id: string, currentlyOpen: boolean) => {
    setExpanded((prev) => ({ ...prev, [id]: !currentlyOpen }));
  };

  const { summary } = bom;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="flex max-h-[90vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-labelledby="bom-preview-title"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 px-6 pt-5 pb-2">
          <div className="min-w-0">
            <div id="bom-preview-title" className="text-lg font-semibold text-slate-900">
              Bill of Materials Preview
            </div>
            <div className="mt-1 text-xs text-slate-500">
              Physical quantities aggregated from the current layout (editor data, not simulation).
              Hangers, power rail and controllers are derived from the drawn track. Costs are rough
              estimates from the default price book unless the catalog or element sets them.
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            aria-label="Close BOM preview"
            title="Close"
          >
            ×
          </button>
        </div>

        <div className="shrink-0 space-y-3 px-6 pb-4">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            BOM Summary
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
            <SummaryStat label="Total Components" value={summary.totalComponents} />
            <SummaryStat
              label="Track Length"
              value={formatBomLengthMeters(summary.trackLengthMeters)}
            />
            <SummaryStat label="Track Sections" value={summary.trackSections} />
            <SummaryStat label="Stockers" value={summary.stockers} />
            {/* <SummaryStat label="Equipment" value={summary.equipment} /> */}
            <SummaryStat label="OHTs" value={summary.ohts} />
            <SummaryStat label="Junctions" value={summary.junctions} />
            <SummaryStat
              label="Est. Total"
              value={formatBomCurrency(summary.estimatedTotalCost, summary.currency)}
            />
            <SummaryStat
              label="Priced Rows"
              value={`${summary.pricedRows} / ${summary.pricedRows + summary.unpricedRows}`}
            />
          </div>

          <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <svg
                className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-slate-400"
                width="14"
                height="14"
                viewBox="0 0 20 20"
                fill="none"
                aria-hidden
              >
                <circle cx="8.5" cy="8.5" r="5.5" stroke="currentColor" strokeWidth="1.6" />
                <path
                  d="M13 13l4 4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <input
                type="search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search components..."
                className="w-full rounded-lg border border-slate-200 bg-white py-2 pr-3 pl-9 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-sky-400"
                aria-label="Search BOM components"
              />
            </div>
            <div
              className="flex flex-wrap gap-1"
              role="tablist"
              aria-label="Filter BOM by category"
            >
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={filter === option}
                  onClick={() => setFilter(option)}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                    filter === option
                      ? 'bg-blue-600 text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-2 pb-2">
          {visibleRows.length === 0 ? (
            <div className="px-5 py-10 text-center text-sm text-slate-500">
              {bom.rows.length === 0
                ? 'No BOM-relevant components have been placed on the layout yet.'
                : 'No rows match the current filter or search.'}
            </div>
          ) : (
            <table className="min-w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white text-xs text-slate-400">
                <tr className="border-b border-slate-100">
                  <th className="w-10 px-4 py-2 text-left font-medium">#</th>
                  <th className="min-w-[180px] px-3 py-2 text-left font-medium">Component</th>
                  <th className="px-3 py-2 text-left font-medium">Category</th>
                  <th className="min-w-[120px] px-3 py-2 text-left font-medium">Type / Spec</th>
                  <th className="px-3 py-2 text-right font-medium">Qty</th>
                  <th className="px-3 py-2 text-left font-medium">Unit</th>
                  <th className="min-w-[88px] px-3 py-2 text-right font-medium">Total Length</th>
                  <th className="min-w-[110px] px-3 py-2 text-left font-medium">Part No.</th>
                  <th className="min-w-[90px] px-3 py-2 text-right font-medium">Unit Cost</th>
                  <th className="min-w-[100px] px-3 py-2 text-right font-medium">Ext. Cost</th>
                  <th className="px-3 py-2 text-left font-medium">Source</th>
                </tr>
              </thead>
              <tbody className="text-slate-700">
                {visibleRows.map((row) => {
                  const installation = row.installationComponents ?? [];
                  const canExpand = installation.length > 0;
                  const isOpen = Boolean(expanded[row.id]);
                  return (
                    <React.Fragment key={row.id}>
                      <tr className="border-b border-slate-100">
                        <td className="px-4 py-3 tabular-nums whitespace-nowrap text-slate-500">
                          {row.itemNo}
                        </td>
                        <td className="px-3 py-3 font-medium text-slate-900">
                          {canExpand ? (
                            <button
                              type="button"
                              onClick={() => toggleExpanded(row.id, isOpen)}
                              className="inline-flex items-center gap-2 text-left"
                              aria-expanded={isOpen}
                            >
                              <Chevron open={isOpen} />
                              {row.component}
                            </button>
                          ) : (
                            <span className="inline-flex items-center gap-2 pl-5">
                              {row.component}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3">
                          <CategoryPill category={row.category} />
                        </td>
                        <td
                          className="px-3 py-3 text-slate-500"
                          title={row.derivedFrom ?? row.specification}
                        >
                          {row.specification}
                          {row.derivedFrom ? (
                            <div className="mt-0.5 text-[10px] text-slate-400">
                              {row.derivedFrom}
                            </div>
                          ) : null}
                        </td>
                        <td className="px-3 py-3 text-right font-medium tabular-nums">
                          {row.quantity}
                        </td>
                        <td className="px-3 py-3 text-slate-500">{row.unit}</td>
                        <td className="px-3 py-3 text-right tabular-nums whitespace-nowrap text-slate-500">
                          {formatBomLengthMeters(row.totalLengthMeters)}
                        </td>
                        <td
                          className="px-3 py-3 font-mono text-xs text-slate-700"
                          title={
                            row.manufacturer !== '—'
                              ? `${row.manufacturer} · lead ${row.leadTime}`
                              : undefined
                          }
                        >
                          {row.partNumber}
                        </td>
                        <td
                          className="px-3 py-3 text-right tabular-nums whitespace-nowrap text-slate-500"
                          title={row.costSource ? COST_SOURCE_LABEL[row.costSource] : undefined}
                        >
                          {row.unitCost}
                        </td>
                        <td className="px-3 py-3 text-right font-medium tabular-nums whitespace-nowrap text-slate-900">
                          {formatBomCurrency(row.extendedCostValue ?? null, summary.currency)}
                        </td>
                        <td className="px-3 py-3 capitalize text-slate-500">{row.source}</td>
                      </tr>
                      {canExpand && isOpen ? (
                        <tr>
                          <td colSpan={11} className="p-0">
                            <InstallationComponentsPanel itemNo={row.itemNo} items={installation} />
                          </td>
                        </tr>
                      ) : null}
                    </React.Fragment>
                  );
                })}
              </tbody>
              <tfoot className="sticky bottom-0 bg-slate-50 text-xs">
                <tr className="border-t border-slate-200">
                  <td colSpan={9} className="px-4 py-2.5 text-right font-semibold text-slate-700">
                    Estimated total ({summary.pricedRows} priced of{' '}
                    {summary.pricedRows + summary.unpricedRows} rows)
                  </td>
                  <td className="px-3 py-2.5 text-right text-sm font-semibold tabular-nums whitespace-nowrap text-slate-900">
                    {formatBomCurrency(summary.estimatedTotalCost, summary.currency)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          )}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-100 bg-white px-6 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-50"
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleExportCsv}
            disabled={bom.rows.length === 0}
            className={`rounded-lg px-3 py-1.5 text-sm font-semibold text-white ${
              bom.rows.length === 0
                ? 'cursor-not-allowed bg-slate-300'
                : 'bg-blue-600 hover:bg-blue-700'
            }`}
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

export default BomPreviewModal;
